// components/OpeningSelect.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Pressable,
  FlatList,
  Animated,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { COLORS, SPACING, RADIUS, SIZES, GRADIENTS, SHADOWS } from '../theme';
import SvgPiece from './SvgPiece';

// --- Data ---
const DESCRIPTION = 'Popular e4 opening,\nfocusing on development\nand control.';

const RAW_OPENINGS = [
  { name: 'Ruy Lopez', pgn: '1. e4 e5 2. Nf3 Nc6 3. Bb5', piece: 'b' },
  { name: 'Sicilian Defense', pgn: '1. e4 c5', piece: 'p' },
  { name: 'French Defense', pgn: '1. e4 e6', piece: 'p' },
  { name: "Queen's Gambit", pgn: '1. d4 d5 2. c4', piece: 'p' },
  { name: "King's Indian", pgn: '1. d4 Nf6 2. c4 g6', piece: 'n' },
  { name: 'Caro-Kann', pgn: '1. e4 c6', piece: 'p' },
  { name: 'English Opening', pgn: '1. c4', piece: 'p' },
  { name: 'Pirc Defense', pgn: '1. e4 d6', piece: 'n' },
  { name: 'Scandinavian Defense', pgn: '1. e4 d5', piece: 'q' },
  { name: "Alekhine's Defense", pgn: '1. e4 Nf6', piece: 'n' },
  { name: 'Italian Game', pgn: '1. e4 e5 2. Nf3 Nc6 3. Bc4', piece: 'b' },
  { name: 'Slav Defense', pgn: '1. d4 d5 2. c4 c6', piece: 'p' },
];

const OPENINGS = RAW_OPENINGS.flatMap((o) => [
  { ...o, color: 'white', playerColor: 'w', description: DESCRIPTION },
  { ...o, color: 'black', playerColor: 'b', description: DESCRIPTION },
]);

// Deterministic “progress”
const hash01 = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
};
const progressFor = (name, color) => 0.22 + hash01(`${name}:${color}`) * 0.62;

// Responsive square grid
const computeGrid = (containerWidth) => {
  const w = Math.max(0, containerWidth);
  const gap = SIZES.gridGap;

  let cols = Math.max(2, Math.ceil((w + gap) / (SIZES.cardMax + gap)));
  const sizeFor = (c) => (w - gap * (c - 1)) / c;

  while (cols > 2 && sizeFor(cols) < SIZES.cardMin) cols -= 1;
  const cardSize = Math.floor(sizeFor(cols));

  return { cols, cardSize };
};

export default function OpeningSelect({ onSelectOpening }) {
  const [selectedColor, setSelectedColor] = useState('white');
  const [query, setQuery] = useState('');

  const { width: windowWidth } = useWindowDimensions();
  const contentWidth = Math.max(0, windowWidth - SPACING.md * 2);
  const { cols, cardSize } = useMemo(() => computeGrid(contentWidth), [contentWidth]);

  const thumbX = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(thumbX, {
      toValue: selectedColor === 'white' ? 0 : SIZES.toggleThumbTravel,
      duration: 170,
      useNativeDriver: true,
    }).start();
  }, [selectedColor, thumbX]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return OPENINGS.filter((o) => {
      if (o.color !== selectedColor) return false;
      if (!q) return true;
      return o.name.toLowerCase().includes(q);
    });
  }, [selectedColor, query]);

  return (
    <View style={styles.screen}>
      {/* Search */}
      <View style={styles.searchWrap}>
        <TextInput
          value={query}
          onChangeText={setQuery}
          placeholder="Search Openings..."
          placeholderTextColor={COLORS.textMuted}
          style={styles.searchInput}
          autoCorrect={false}
          autoCapitalize="none"
        />
        <View pointerEvents="none" style={styles.searchIconWrap}>
          <Text style={styles.searchIcon}>⌕</Text>
        </View>
      </View>

      {/* Toggle */}
      <View style={styles.togglePill}>
        <View pointerEvents="none" style={styles.toggleGradientBase} />
        <View
          pointerEvents="none"
          style={[
            styles.toggleActiveTint,
            selectedColor === 'white' ? styles.toggleActiveLeft : styles.toggleActiveRight,
          ]}
        />

        <Pressable
          onPress={() => setSelectedColor('white')}
          style={({ hovered, pressed }) => [styles.toggleSide, (hovered || pressed) && styles.toggleSideHover]}
          accessibilityRole="button"
          accessibilityLabel="Select White openings"
        >
          <Text style={[styles.toggleText, selectedColor === 'white' ? styles.toggleTextActive : styles.toggleTextInactive]}>
            White <Text style={styles.toggleKing}>♔</Text>
          </Text>
        </Pressable>

        <Pressable
          onPress={() => setSelectedColor((c) => (c === 'white' ? 'black' : 'white'))}
          style={styles.centerSwitch}
          accessibilityRole="switch"
          accessibilityState={{ checked: selectedColor === 'black' }}
          accessibilityLabel="Toggle between White and Black openings"
        >
          <View style={styles.switchTrack} />
          <Animated.View style={[styles.switchThumb, { transform: [{ translateX: thumbX }] }]} />
        </Pressable>

        <Pressable
          onPress={() => setSelectedColor('black')}
          style={({ hovered, pressed }) => [styles.toggleSide, (hovered || pressed) && styles.toggleSideHover]}
          accessibilityRole="button"
          accessibilityLabel="Select Black openings"
        >
          <Text style={[styles.toggleText, selectedColor === 'black' ? styles.toggleTextActive : styles.toggleTextInactive]}>
            Black <Text style={styles.toggleKing}>♚</Text>
          </Text>
        </Pressable>
      </View>

      {/* Grid / Empty */}
      {filtered.length > 0 ? (
        <FlatList
          key={String(cols)}
          data={filtered}
          numColumns={cols}
          keyExtractor={(item, idx) => `${item.name}-${item.color}-${idx}`}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.gridContent}
          renderItem={({ item, index }) => (
            <OpeningCard
              item={item}
              index={index}
              size={cardSize}
              cols={cols}
              onPress={() => onSelectOpening && onSelectOpening(item)}
            />
          )}
        />
      ) : (
        <EmptyState />
      )}
    </View>
  );
}

function OpeningCard({ item, index, size, cols, onPress }) {
  const isWhite = item.color === 'white';
  const gap = SIZES.gridGap;

  const isLastInRow = (index % cols) === cols - 1;
  const cellStyle = {
    width: size,
    height: size,
    marginRight: isLastInRow ? 0 : gap,
    marginBottom: gap,
  };

  const progress = progressFor(item.name, item.color);

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }) => [
        cellStyle,
        styles.card,
        isWhite ? styles.cardWhite : styles.cardBlack,
        (hovered || pressed) && styles.cardHover,
      ]}
      accessibilityRole="button"
      accessibilityLabel={`${item.name}, ${isWhite ? 'White' : 'Black'}`}
    >
      {/* Native overlays (to mimic the web gradients closely) */}
      <View pointerEvents="none" style={styles.cardOverlayBase} />
      <View pointerEvents="none" style={[styles.cardTintA, isWhite ? styles.cardTintAWhite : styles.cardTintABlack]} />
      <View pointerEvents="none" style={styles.cardTintB} />
      <View pointerEvents="none" style={styles.cardSpot} />

      {/* Badge icon */}
      <View style={styles.cardBadge}>
        <SvgPiece piece={{ type: item.piece, color: isWhite ? 'w' : 'b' }} size="78%" />
      </View>

      <View style={styles.cardBody}>
        <Text style={styles.cardTitle} numberOfLines={1}>
          {item.name}
        </Text>
        <Text style={styles.cardSubtitle}>{isWhite ? 'White' : 'Black'}</Text>

        <Text style={styles.cardDesc} numberOfLines={3}>
          {item.description}
        </Text>

        <View style={styles.progressWrap}>
          <View style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]} />
        </View>
      </View>
    </Pressable>
  );
}

function EmptyState() {
  return (
    <View style={styles.emptyWrap}>
      <View pointerEvents="none" style={styles.emptyPieceWrap}>
        <SvgPiece piece={{ type: 'q', color: 'w' }} size="100%" />
      </View>
      <Text style={styles.emptyTitle}>No openings found</Text>
      <Text style={styles.emptySubtitle}>There are no openings found illustration.</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
  },

  // Search
  searchWrap: {
    height: SIZES.searchHeight,
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.glass,
    borderWidth: 1,
    borderColor: COLORS.border,
    paddingHorizontal: 14,
    justifyContent: 'center',
    marginBottom: 12,
    position: 'relative',
    ...Platform.select({
      web: { backdropFilter: 'blur(10px)' },
      default: {},
    }),
  },
  searchInput: {
    color: COLORS.text,
    fontSize: 13,
    paddingRight: 26,
    paddingVertical: 0,
  },
  searchIconWrap: {
    position: 'absolute',
    right: 10,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  searchIcon: {
    color: 'rgba(255,255,255,0.68)',
    fontSize: 16,
  },

  // Toggle
  togglePill: {
    height: SIZES.toggleHeight,
    borderRadius: RADIUS.pill,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.25)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
    overflow: 'hidden',
    backgroundColor: COLORS.woodMid,
    ...Platform.select({
      web: { boxShadow: '0px 8px 18px rgba(0,0,0,0.35)' },
      default: {
        shadowColor: '#000',
        shadowOpacity: 0.35,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 6,
      },
    }),
  },
  toggleGradientBase: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: COLORS.woodMid,
    ...Platform.select({
      web: { backgroundImage: GRADIENTS.togglePillWeb },
      default: {},
    }),
  },
  toggleActiveTint: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '44%',
    borderRadius: RADIUS.pill,
    opacity: 0.30,
  },
  toggleActiveLeft: { left: 0, backgroundColor: 'rgba(0,0,0,0.22)' },
  toggleActiveRight: { right: 0, backgroundColor: 'rgba(0,0,0,0.16)' },

  toggleSide: { paddingHorizontal: 10, height: '100%', justifyContent: 'center' },
  toggleSideHover: { opacity: 0.92 },
  toggleText: { fontSize: 13, fontWeight: '800', letterSpacing: 0.2 },
  toggleTextActive: { color: 'rgba(255,255,255,0.92)' },
  toggleTextInactive: { color: 'rgba(255,255,255,0.78)' },
  toggleKing: { fontSize: 13, opacity: 0.95 },

  centerSwitch: {
    width: SIZES.toggleSwitchW,
    height: SIZES.toggleSwitchH,
    borderRadius: RADIUS.pill,
    backgroundColor: COLORS.switchTrack,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  switchTrack: { ...StyleSheet.absoluteFillObject, borderRadius: RADIUS.pill },
  switchThumb: {
    width: SIZES.toggleThumb,
    height: SIZES.toggleThumb,
    borderRadius: RADIUS.pill,
    backgroundColor: COLORS.switchThumb,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.25)',
  },

  // Grid
  gridContent: { paddingBottom: 18 },

  // Card
  card: {
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    overflow: 'hidden',
    padding: 12,
    backgroundColor: COLORS.glass2,
    ...SHADOWS.card,
    ...Platform.select({
      web: { backdropFilter: 'blur(10px)' },
      default: {},
    }),
  },
  cardWhite: Platform.select({
    web: { backgroundImage: GRADIENTS.cardWhiteWeb },
    default: {},
  }),
  cardBlack: Platform.select({
    web: { backgroundImage: GRADIENTS.cardBlackWeb },
    default: {},
  }),
  cardHover: { transform: [{ translateY: -1 }] },

  // Native overlays (to mimic the web gradient layers)
  cardOverlayBase: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: COLORS.glass2,
  },
  cardTintA: {
    position: 'absolute',
    top: -44,
    left: -68,
    width: 240,
    height: 150,
    borderRadius: RADIUS.pill,
    transform: [{ rotate: '-18deg' }],
    opacity: 0.95,
  },
  cardTintAWhite: { backgroundColor: COLORS.whiteTintA },
  cardTintABlack: { backgroundColor: COLORS.blackTintA },
  cardTintB: {
    position: 'absolute',
    bottom: -56,
    right: -78,
    width: 260,
    height: 175,
    borderRadius: RADIUS.pill,
    transform: [{ rotate: '16deg' }],
    opacity: 0.78,
    backgroundColor: COLORS.tintB,
  },
  cardSpot: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 92,
    height: 92,
    borderRadius: RADIUS.pill,
    opacity: 0.22,
    backgroundColor: COLORS.spot,
  },

  cardBadge: {
    width: SIZES.badge,
    height: SIZES.badge,
    borderRadius: RADIUS.pill,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.28)',
    backgroundColor: 'rgba(255,255,255,0.10)',
  },

  cardBody: { flex: 1 },
  cardTitle: { color: COLORS.text, fontSize: 15, fontWeight: '900', marginBottom: 2 },
  cardSubtitle: { color: 'rgba(255,255,255,0.78)', fontSize: 12, fontWeight: '700', marginBottom: 8 },
  cardDesc: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 10.5,
    lineHeight: 13.5,
    fontWeight: '600',
    marginBottom: 10,
  },

  progressWrap: {
    height: 5,
    width: '62%',
    borderRadius: RADIUS.pill,
    backgroundColor: 'rgba(255,255,255,0.12)',
    overflow: 'hidden',
    marginTop: 'auto',
  },
  progressFill: {
    height: '100%',
    borderRadius: RADIUS.pill,
    backgroundColor: 'rgba(255,255,255,0.78)',
  },

  // Empty
  emptyWrap: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingBottom: 40 },
  emptyPieceWrap: { width: SIZES.emptyPiece, height: SIZES.emptyPiece, opacity: 0.18, marginBottom: 10 },
  emptyTitle: { color: COLORS.text, fontSize: 16, fontWeight: '900', marginBottom: 6 },
  emptySubtitle: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 11.5,
    fontWeight: '600',
    textAlign: 'center',
    maxWidth: 260,
  },
});
